/**
 * New doctor and New user (docs/ux/intake.md), both from the doctor list.
 *
 * New doctor asks only for what the record cannot exist without and lands on
 * the doctor's page with six missing checks showing. That is honest: it shows
 * the work remaining rather than pretending a doctor is ready, and it puts the
 * admin where the Fix links are.
 */

import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ApiError, reasonOf } from '@/app/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDoctors } from '../registry/useRegistry';
import { useCreateDoctor, useCreateUser, useDemoPrefill } from './useIntake';

/** The slug rule the server applies, mirrored so the field shows what will be created. */
export function slugFrom(name: string): string {
  return name
    .replace(/^\s*(dr|doctor|prof|professor|mr|mrs|ms|miss)\.?\s+/i, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function NewDoctorDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const id = useId();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prefill = useDemoPrefill();
  const create = useCreateDoctor();

  useEffect(() => {
    if (!open) return;
    setName(''); setSlug(''); setTouchedSlug(false); setError(null);
  }, [open]);

  // The slug follows the name until the admin edits it, and never afterwards.
  const setNameAndSlug = (next: string) => {
    setName(next);
    if (!touchedSlug) setSlug(slugFrom(next));
  };

  async function submit() {
    setError(null);
    if (name.trim().length === 0) { setError('A name is required.'); return; }
    if (!/^[a-z][a-z0-9-]{1,63}$/.test(slug)) { setError('The slug is lower case letters, digits and hyphens, starting with a letter.'); return; }
    try {
      await create.mutateAsync({ slug, name: name.trim() });
      toast.success(`${name.trim()} created. Six checks are missing before they can generate a report.`);
      onOpenChange(false);
      navigate(`/registry/doctors/${slug}`);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'A doctor with that slug exists.' : reasonOf(err) ?? 'The doctor could not be created.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-slot="new-doctor">
        <DialogHeader>
          <DialogTitle>New doctor</DialogTitle>
          <DialogDescription>The record is created with nothing else set. The readiness panel names what is missing.</DialogDescription>
        </DialogHeader>

        {prefill.data ? (
          <div>
            <Button type="button" variant="outline" disabled={create.isPending}
              onClick={() => { setNameAndSlug(prefill.data!.doctor.name); setSlug(prefill.data!.doctor.slug); setTouchedSlug(true); }}>
              Fill from demo files
            </Button>
          </div>
        ) : null}

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input id={`${id}-name`} value={name} onChange={(e) => setNameAndSlug(e.target.value)} placeholder="Dr Jane Smith" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-slug`}>Slug</Label>
            <Input id={`${id}-slug`} value={slug} onChange={(e) => { setTouchedSlug(true); setSlug(e.target.value); }} placeholder="jane-smith" />
            <p className="text-[13px] text-beam-text-soft">Permanent. Every other record refers to the doctor by this.</p>
          </div>
        </div>

        {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>Cancel</Button>
          <Button type="button" data-primary="true" onClick={() => void submit()} disabled={create.isPending}>{create.isPending ? 'Creating' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * New user. A doctor must name the registry record they belong to, because a
 * sign-in without it resolves to a persona with no record: the defect
 * tools/cognito-users.sh has, since it cannot set the claim at all.
 */
export function NewUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const id = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [persona, setPersona] = useState<'admin' | 'qa' | 'doctor'>('doctor');
  const [doctorSlug, setDoctorSlug] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const doctors = useDoctors();
  const create = useCreateUser();

  useEffect(() => {
    if (!open) return;
    setName(''); setEmail(''); setPersona('doctor'); setDoctorSlug(''); setPassword(''); setError(null);
  }, [open]);

  const needsDoctor = persona === 'doctor' && !doctorSlug;

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), email: email.trim(), persona, doctorSlug: persona === 'doctor' ? doctorSlug : null, password });
      toast.success(`${email.trim()} can sign in as ${persona}.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'A user with that email exists in the pool.' : reasonOf(err) ?? 'The sign-in could not be created.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-slot="new-user">
        <DialogHeader>
          <DialogTitle>New user</DialogTitle>
          <DialogDescription>Demo builds only. The password is set permanently and is not changed at first sign-in.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-email`}>Email</Label>
            <Input id={`${id}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-persona`}>Role</Label>
            <Select value={persona} onValueChange={(v) => setPersona(v as 'admin' | 'qa' | 'doctor')}>
              <SelectTrigger id={`${id}-persona`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="qa">QA</SelectItem>
                <SelectItem value="doctor">Doctor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {persona === 'doctor' ? (
            <div className="grid gap-1.5">
              <Label htmlFor={`${id}-record`}>Doctor record</Label>
              <Select value={doctorSlug} onValueChange={setDoctorSlug}>
                <SelectTrigger id={`${id}-record`}><SelectValue placeholder="Choose the record this sign-in belongs to" /></SelectTrigger>
                <SelectContent>
                  {(doctors.data ?? []).map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[13px] text-beam-text-soft">A doctor sign-in without its record cannot be acted as, or attributed.</p>
            </div>
          ) : null}
          <div className="grid gap-1.5">
            <Label htmlFor={`${id}-password`}>Password</Label>
            <Input id={`${id}-password`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            <p className="text-[13px] text-beam-text-soft">At least 12 characters. Hand it over yourself; it is not emailed.</p>
          </div>
        </div>

        {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>Cancel</Button>
          <Button type="button" data-primary="true" onClick={() => void submit()} disabled={create.isPending || needsDoctor || password.length < 12}>
            {create.isPending ? 'Creating' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
